import React, { useState } from 'react';
import { ScrollReveal } from '../components/ScrollReveal';
import { CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image URL
const IMAGES = {
  classroom: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/rqxgj890_IMG_0358.JPG",
};

export default function Enquire() {
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    childName: '',
    childAge: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parent_name: formData.parentName,
          email: formData.email,
          phone: formData.phone,
          child_name: formData.childName,
          child_age: formData.childAge,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly at info@novaroots.ke');
      console.error('Enquiry submission error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="enquire-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <ScrollReveal>
                <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                  Enquire
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                  Contact
                </h1>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <p className="mt-8 text-base font-light leading-relaxed text-[#5C6B6B]">
                  Invitation-only information sessions.<br />
                  Prospectus shared post-session.
                </p>
                <p className="mt-6 text-sm text-[#5C6B6B]">
                  Or email us directly at{' '}
                  <a href="mailto:info@novaroots.ke" className="text-[#C27856] hover:underline">
                    info@novaroots.ke
                  </a>
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={300}>
                <div className="mt-12 img-hover-zoom rounded-2xl overflow-hidden">
                  <img 
                    src={IMAGES.classroom}
                    alt="Nova Roots classroom"
                    className="w-full h-[280px] object-cover"
                  />
                </div>
              </ScrollReveal>
            </div>
            
            <ScrollReveal delay={150}>
              <div className="bg-white p-8 md:p-10 rounded-2xl border border-[#EBE8E0]">
                {isSubmitted ? (
                  <div data-testid="success-message" className="text-center py-16">
                    <div className="w-14 h-14 rounded-full bg-[#1A3C34] flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-7 h-7 text-[#F9F8F4]" />
                    </div>
                    <h3 className="font-heading text-xl font-medium text-[#1A3C34]">
                      Received
                    </h3>
                    <p className="mt-4 text-[#5C6B6B] font-light text-sm">
                      We will be in touch.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="enquiry-form">
                    <h2 className="font-heading text-lg font-medium text-[#1A3C34] mb-8">
                      Enquiry
                    </h2>
                    
                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <div className="space-y-6">
                      <div className="form-field">
                        <label htmlFor="parentName">Parent Name *</label>
                        <input
                          type="text"
                          id="parentName"
                          name="parentName"
                          value={formData.parentName}
                          onChange={handleChange}
                          className="input-nova"
                          required
                          data-testid="input-parent-name"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="input-nova"
                          required
                          data-testid="input-email"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="input-nova"
                          data-testid="input-phone"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label htmlFor="childName">Child's Name *</label>
                        <input
                          type="text"
                          id="childName"
                          name="childName"
                          value={formData.childName}
                          onChange={handleChange}
                          className="input-nova"
                          required
                          data-testid="input-child-name"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label htmlFor="childAge">Child's Age *</label>
                        <input
                          type="text"
                          id="childAge"
                          name="childAge"
                          value={formData.childAge}
                          onChange={handleChange}
                          className="input-nova"
                          placeholder="e.g., 5 years"
                          required
                          data-testid="input-child-age"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label htmlFor="message">Message</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className="input-nova min-h-[120px] resize-none"
                          placeholder="Tell us about your interest in Nova Roots"
                          data-testid="input-message"
                        />
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="mt-8 w-full btn-primary flex items-center justify-center"
                      data-testid="submit-enquiry-btn"
                    >
                      {isLoading ? (
                        <span className="loading-spinner" />
                      ) : (
                        'Submit Enquiry'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
